import { useState, useCallback, useEffect } from "react";
import { useSnapshot } from "valtio";
import "./styles.scss";
import ContentCatalog from "src/modules/contentCatalog/contentCatalog";
import { contentCatalogStore } from "src/modules/contentCatalog/contentCatalogStore";
import "src/modules/contentCatalog/styles.scss";
import { Ad } from "@models/Ad.model";

interface AdPickerProps {
    isOpen: boolean;
    onClose: () => void;
    clientOrganizationId: number;
    serviceProviderOrganizationId: number;
    onSave: (selectedAdIds: number[]) => void;
}

type TabKey = "provider" | "client";

export default function AdPicker({
    isOpen,
    onClose,
    clientOrganizationId,
    serviceProviderOrganizationId,
    onSave,
}: AdPickerProps) {
    const [activeTab, setActiveTab] = useState<TabKey>("provider");
    const { selectedAdIds } = useSnapshot(contentCatalogStore);

    // Reset selection when modal opens
    useEffect(() => {
        if (isOpen) {
            contentCatalogStore.selectedAdIds = [];
        }
    }, [isOpen]);

    const activeOrgId = activeTab === "provider" ? serviceProviderOrganizationId : clientOrganizationId;

    const handleAdClick = useCallback((ad: Ad) => {
        const ids = contentCatalogStore.selectedAdIds;
        const index = ids.indexOf(ad.id);
        if (index >= 0) {
            contentCatalogStore.selectedAdIds = ids.filter((id) => id !== ad.id);
        } else {
            contentCatalogStore.selectedAdIds = [...ids, ad.id];
        }
    }, []);

    const handleConfirm = useCallback(() => {
        onSave([...contentCatalogStore.selectedAdIds]);
        contentCatalogStore.selectedAdIds = [];
        onClose();
    }, [onSave, onClose]);

    const handleCancel = useCallback(() => {
        contentCatalogStore.selectedAdIds = [];
        onClose();
    }, [onClose]);

    if (!isOpen) return null;

    return (
        <>
            <div
                className="modal-backdrop fade show"
                style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
                onClick={handleCancel}
            />
            <div className="modal fade show d-block ad-picker-modal" tabIndex={-1} role="dialog">
                <div className="modal-dialog modal-dialog-scrollable" role="document">
                    <div className="modal-content">
                        {/* Header */}
                        <div className="modal-header">
                            <div className="w-100">
                                <div className="ad-picker-header-content">
                                    <div>
                                        <h5 className="ad-picker-title">Select Ads</h5>
                                        <p className="ad-picker-subtitle">Choose the ads you want to include</p>
                                    </div>
                                    <button type="button" className="close" onClick={handleCancel} aria-label="Close">
                                        <span aria-hidden="true">&times;</span>
                                    </button>
                                </div>
                                <div className="ad-picker-tabs">
                                    <button
                                        className={`ad-picker-tab ${activeTab === "provider" ? "active" : ""}`}
                                        onClick={() => setActiveTab("provider")}
                                    >
                                        Provider Catalog
                                    </button>
                                    <button
                                        className={`ad-picker-tab ${activeTab === "client" ? "active" : ""}`}
                                        onClick={() => setActiveTab("client")}
                                    >
                                        Client Catalog
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Content Catalog */}
                        <div className="ad-picker-catalog-body">
                            <ContentCatalog
                                key={activeOrgId}
                                organizationId={activeOrgId}
                                onAdClick={handleAdClick}
                                adMenuItems={() => []}
                                folderMenuItems={() => []}
                                collectionMenuItems={() => []}
                                collectionAdMenuItems={() => []}
                                showCollections={false}
                                enableDnd={false}
                            />
                        </div>

                        {/* Footer */}
                        <div className="ad-picker-footer">
                            <button type="button" className="btn-secondary" onClick={handleCancel}>
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn-success"
                                onClick={handleConfirm}
                                disabled={selectedAdIds.length === 0}
                            >
                                Confirm Selection ({selectedAdIds.length})
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
